import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import projects from '../data/projects';
import { buildCreativeWorkSchema } from '../utils/seo';

const CaseStudyPage = () => {
  const { slug } = useParams();
  const project = projects.find((item) => item.slug === slug);

  if (!project) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white px-6 py-24">
        <Helmet>
          <title>Project Not Found | O'Brian Baxter</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <h1 className="text-5xl font-black">Case Study Not Found</h1>
        <p className="mt-4 text-zinc-400">The requested case study does not exist.</p>
        <Link to="/" className="mt-6 inline-block text-[#00f3ff]">Return home</Link>
      </div>
    );
  }

  const pageUrl = `${window.location.origin}/#${window.location.pathname}`;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f7f6ed]">
      <Helmet>
        <title>{project.title} | O'Brian Baxter</title>
        <meta name="description" content={project.description} />
        <script type="application/ld+json">{JSON.stringify(buildCreativeWorkSchema({
            title: project.title,
            description: project.description,
            authorName: "O'Brian Baxter",
            url: pageUrl,
            image: project.image
          }))}</script>
      </Helmet>

      <section className="py-24 px-6 max-w-6xl mx-auto">
        <motion.h1 initial={{opacity:0,y:30}} animate={{opacity:1,y:0}} className="text-6xl font-black tracking-tight uppercase">
          {project.title}
        </motion.h1>
        <p className="mt-6 text-zinc-400 tracking-wide leading-relaxed">{project.description}</p>
        <div className="mt-8 flex flex-wrap gap-4">
          <span className="text-xs uppercase tracking-widest text-[#00f3ff] bg-white/10 px-3 py-1 rounded">{project.tag}</span>
          <span className="text-xs uppercase tracking-widest text-zinc-300 bg-white/5 px-3 py-1 rounded">{project.location}</span>
        </div>
        <a target="_blank" rel="noopener noreferrer" href={project.link} className="mt-10 inline-block bg-[#00f3ff] text-black uppercase px-8 py-3 font-bold">Live Site</a>
        <Link to="/" className="mt-10 inline-block ml-4 text-[#00f3ff] underline">Back to Home</Link>

        <div className="mt-16 grid md:grid-cols-3 gap-8">
          <article className="p-6 bg-zinc-900/40 border border-white/10 rounded-xl">
            <h3 className="text-sm uppercase tracking-widest text-zinc-300">Year</h3>
            <p className="text-2xl font-bold">{project.year}</p>
          </article>
          <article className="p-6 bg-zinc-900/40 border border-white/10 rounded-xl">
            <h3 className="text-sm uppercase tracking-widest text-zinc-300">Roles</h3>
            <ul className="mt-2 space-y-2 text-sm text-zinc-300">
              {project.roles.map((role) => <li key={role}>• {role}</li>)}
            </ul>
          </article>
          <article className="p-6 bg-zinc-900/40 border border-white/10 rounded-xl">
            <h3 className="text-sm uppercase tracking-widest text-zinc-300">Outcome</h3>
            <p className="mt-2 text-sm text-zinc-300">{project.outcome}</p>
          </article>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyPage;
